import { RichTextHelper } from 'src/app/core/helpers/rich-text.helper';
import { AbstractControl, FormArray, FormGroup, UntypedFormArray, UntypedFormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';

/**
 * Helper com métodos estáticos para validação e manipulação de formulários reativos.
 */
export class FormHelper {
  /** Marca todos os campos do formulário como dirty e touched (recursivamente). */
  static verificaValidacoesForm(formGroup: UntypedFormGroup | UntypedFormArray): void {
    Object.keys(formGroup.controls).forEach(campo => {
      const controle = formGroup.get(campo);
      controle?.markAsDirty();
      controle?.markAsTouched();
      if (controle instanceof UntypedFormGroup || controle instanceof UntypedFormArray) {
        FormHelper.verificaValidacoesForm(controle);
      }
    });
  }

  /** Limpa as validações de todos os controles de forma recursiva. */
  static clearValidatorsRecursively(group: FormGroup): void {
    Object.keys(group.controls).forEach(key => {
      const control = group.get(key);
      if (control instanceof FormGroup) {
        FormHelper.clearValidatorsRecursively(control);
      } else {
        control?.clearValidators();
        control?.updateValueAndValidity();
      }
    });
  }

  /** Retorna a lista de nomes dos controles inválidos (recursivamente). */
  static checkInvalidControls(control: FormGroup | FormArray): string[] {
    const invalidControls: string[] = [];
    Object.keys(control.controls).forEach(key => {
      const child = control.get(key);
      if (child instanceof FormGroup || child instanceof FormArray) {
        invalidControls.push(...FormHelper.checkInvalidControls(child));
      } else if (child && child.invalid) {
        invalidControls.push(key);
      }
    });
    return invalidControls;
  }

  /** Permite apenas dígitos numéricos (uso em templates: `(keypress)="numbersOnly($event)"`). */
  static numbersOnly(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      return false;
    }
    return true;
  }

  /** Verifica se o caractere digitado está na lista de permitidos (uso em templates: `(keypress)="verificarCaracter($event, '...')"`). */
  static verificarCaracter(event: KeyboardEvent, caracteresPermitidos: string): void {
    const inputChar = event.key;
    // Verifica se o caractere está na lista de permitidos ou se é uma tecla de controle
    if (!caracteresPermitidos.includes(inputChar) && !FormHelper.teclaControle(event)) {
      event.preventDefault();
    }
  }

  /** Permite teclas de controle como Backspace, Delete, Setas, etc. */
  private static teclaControle(event: KeyboardEvent): boolean {
    return event.key === 'Backspace' || event.key === 'ArrowLeft' || event.key === 'ArrowRight' || event.key === 'Delete' || event.key === 'Enter';
  }

  /** Remove tags HTML de uma string, preservando o texto puro. */
  static stripHtml(value: string): string {
    if (!value) return '';
    return RichTextHelper.visibleText(String(value));
  }

  /** Conta a representação persistida pelo DAO: tags não inserem linhas e entidades não são decodificadas. */
  static persistedRichTextLength(value: unknown): number {
    const texto = String(value ?? '').replace(/\0/g, '')
      .replace(/<!--[\s\S]*?(?:-->|$)|<\?[\s\S]*?(?:\?>|$)|<(?!\s)(?:[^<>"']|"[^"]*"|'[^']*')*(?:>|$)/g, '')
      .replace(/^[ \t\n\r\x0B]+|[ \t\n\r\x0B]+$/g, '');
    return Array.from(texto).length;
  }

  static maxLengthPersistedRichTextValidator(limite: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const atual = FormHelper.persistedRichTextLength(control.value);
      return atual > limite ? { maxLengthPersistedRichText: { limite, atual } } : null;
    };
  }

  /** Normaliza o conteúdo de um rich text: strip HTML, remove quebras de linha e colapsa espaços múltiplos. */
  static normalizeRichText(value: string): string {
    if (!value) return '';
    return RichTextHelper.visibleText(String(value));
  }

  /** Retorna true se o conteúdo do rich text (sem HTML) exceder o limite de caracteres ou contiver excesso de quebras lógicas. */
  static richTextExcedeLimite(value: string, limit = 300): boolean {
    const rawValue = value ?? '';
    const normalized = FormHelper.normalizeRichText(rawValue);
    
    if (Array.from(normalized).length > limit) return true;

    // Conta elementos de bloco que quebram a linha visualmente
    const blockTagsMatch = rawValue.match(/<(br|p|li|div|h[1-6])[>\s]/gi);
    const blockCount = blockTagsMatch ? blockTagsMatch.length : 0;
    
    // Se tiver mais de 4 linhas/quebras visuais, aciona o limite para evitar estouro de altura
    return blockCount > 4;
  }

  /** Retorna uma string formatada com a contagem e o rótulo no singular ou plural. Ex: "1 caracter", "5 caracteres". */
  static contadorRichText(value: string): string {
    const n = RichTextHelper.count(value ?? '');
    return `${n} ${n <= 1 ? 'caracter' : 'caracteres'}`;
  }

  /** Valida se um campo de rich text possui conteúdo mínimo (ignora quebras de linha e espaços múltiplos). */
  static minLengthRichTextValidator(minLength: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      const normalized = FormHelper.normalizeRichText(String(control.value));
      if (RichTextHelper.byteLength(RichTextHelper.sanitizeHtml(String(control.value))) > RichTextHelper.MAX_BYTES) return { maxLengthRichText: true };
      return Array.from(normalized).length >= minLength ? null : { minLengthRichText: true };
    };
  }

  /** Valida CPF informado sem depender da máscara aplicada no campo. */
  static cpfValidator(control: AbstractControl): ValidationErrors | null {
    const cpf = String(control.value ?? '').replace(/\D/g, '');
    if (cpf.length === 0) return null;
    if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return { cpf: true };

    let soma = 0;
    for (let indice = 0; indice < 9; indice += 1) soma += Number(cpf[indice]) * (10 - indice);
    const primeiroDigito = (soma * 10) % 11 % 10;
    if (primeiroDigito !== Number(cpf[9])) return { cpf: true };

    soma = 0;
    for (let indice = 0; indice < 10; indice += 1) soma += Number(cpf[indice]) * (11 - indice);
    const segundoDigito = (soma * 10) % 11 % 10;
    return segundoDigito === Number(cpf[10]) ? null : { cpf: true };
  }

  /** Valida CNPJ informado sem depender da máscara aplicada no campo. */
  static cnpjValidator(control: AbstractControl): ValidationErrors | null {
    const cnpj = String(control.value ?? '').replace(/\D/g, '');
    if (cnpj.length === 0) return null;
    if (cnpj.length !== 14 || /^(\d)\1{13}$/.test(cnpj)) return { cnpj: true };

    const calcularDigito = (base: string): number => {
      let peso = base.length - 7;
      let soma = 0;
      for (let indice = 0; indice < base.length; indice += 1) {
        soma += Number(base[indice]) * peso;
        peso -= 1;
        if (peso < 2) peso = 9;
      }
      const resto = soma % 11;
      return resto < 2 ? 0 : 11 - resto;
    };

    const primeiroDigito = calcularDigito(cnpj.slice(0, 12));
    const segundoDigito = calcularDigito(cnpj.slice(0, 12) + primeiroDigito);
    return primeiroDigito === Number(cnpj[12]) && segundoDigito === Number(cnpj[13]) ? null : { cnpj: true };
  }

  /** Aceita exclusivamente CPF ou CNPJ semanticamente válidos. */
  static cpfCnpjValidator(control: AbstractControl): ValidationErrors | null {
    const identificador = String(control.value ?? '').replace(/\D/g, '');
    if (identificador.length === 0) return null;
    if (identificador.length === 11) return FormHelper.cpfValidator(control);
    if (identificador.length === 14) return FormHelper.cnpjValidator(control);
    return { cpfCnpj: true };
  }

  /** Valida se o campo de confirmação confere com o campo 'senha' do mesmo grupo. */
  static senhasConferemValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const senha = control.parent?.get('senha')?.value;
      return senha && control.value && senha !== control.value ? { senhasDiferentes: true } : null;
    };
  }
}
